import React, { useState, useEffect, useContext } from 'react';
import Layout from '../components/Layout';
import { getMaintenanceJobs } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Plus, Wrench, AlertCircle, Clock, Bot, ImageIcon, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import AddMaintenanceModal from '../components/modals/AddMaintenanceModal';
import JobDetailModal from '../components/modals/JobDetailModal';
import { AuthContext } from '../contexts/AuthContext';

export default function Maintenance() {
  const [jobs, setJobs] = useState([]);
  const [historyJobs, setHistoryJobs] = useState([]);
  const [pmReviewJobs, setPmReviewJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [activeTab, setActiveTab] = useState('active'); // 'active', 'history', 'pm_review'
  const { user } = useContext(AuthContext);

  useEffect(() => {
    loadJobs();
    loadHistory();
    if (user?.role === 'agent' || user?.role === 'admin') {
      loadPMReviewJobs();
    }
  }, [user]);

  const loadJobs = async () => {
    try {
      const data = await getMaintenanceJobs();
      setJobs(data);
    } catch (error) {
      toast.error('Failed to load maintenance jobs');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/maintenance-jobs/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setHistoryJobs(data);
    } catch (error) {
      console.error('Failed to load history:', error);
    }
  };

  const loadPMReviewJobs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/maintenance-jobs/pm-review-required`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setPmReviewJobs(data);
    } catch (error) {
      console.error('Failed to load PM review jobs:', error);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'emergency': return 'badge-danger';
      case 'high': return 'badge-warning';
      case 'medium': return 'badge-info';
      case 'low': return 'badge-success';
      default: return 'badge-info';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'badge-success';
      case 'in_progress': return 'badge-info';
      case 'assigned': return 'badge-warning';
      case 'pending': return 'badge-danger';
      default: return 'badge-info';
    }
  };

  const isPMRole = user?.role === 'agent' || user?.role === 'admin';

  return (
    <Layout>
      <div data-testid="maintenance-page" className="max-w-7xl mx-auto animate-fade-in">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Maintenance</h1>
            <p className="text-gray-600">AI-coordinated maintenance workflow</p>
          </div>
          <Button data-testid="new-job-btn" className="gap-2" onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4" />
            New Job
          </Button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex gap-6">
            <button
              onClick={() => setActiveTab('active')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'active'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Active Jobs
              {jobs.length > 0 && (
                <span className="ml-2 bg-blue-100 text-blue-600 py-0.5 px-2 rounded-full text-xs font-semibold">
                  {jobs.length}
                </span>
              )}
            </button>

            {isPMRole && pmReviewJobs.length > 0 && (
              <button
                onClick={() => setActiveTab('pm_review')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'pm_review'
                    ? 'border-red-600 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                🚨 PM Action Required
                <span className="ml-2 bg-red-100 text-red-600 py-0.5 px-2 rounded-full text-xs font-semibold">
                  {pmReviewJobs.length}
                </span>
              </button>
            )}

            <button
              onClick={() => setActiveTab('history')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'history'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              History
              {historyJobs.length > 0 && (
                <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs font-semibold">
                  {historyJobs.length}
                </span>
              )}
            </button>
          </nav>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : activeTab === 'active' ? (
          jobs.length === 0 ? (
            <Card data-testid="empty-state">
              <CardContent className="flex flex-col items-center justify-center py-20">
                <Wrench className="w-16 h-16 text-gray-300 mb-4" />
                <p className="text-gray-500 text-lg mb-4">No active maintenance jobs</p>
                <Button data-testid="create-first-job-btn" className="gap-2" onClick={() => setShowAddModal(true)}>
                  <Plus className="w-4 h-4" />
                  Create First Job
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {jobs.map((job) => (
              <Card 
                key={job.id} 
                data-testid={`job-card-${job.id}`} 
                className="card-hover cursor-pointer"
                onClick={() => {
                  setSelectedJob(job);
                  setShowDetailModal(true);
                }}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Wrench className="w-5 h-5 text-orange-600" />
                      <h3 className="font-semibold text-gray-900">{job.title}</h3>
                      {job.ai_created && (
                        <Bot className="w-4 h-4 text-purple-500" title="AI Created" />
                      )}
                    </div>
                    {job.requires_owner_approval && !job.owner_approved && (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                        Approval Required
                      </span>
                    )}
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">{job.description}</p>
                  
                  <div className="flex items-center gap-2 flex-wrap mb-3">
                    <span className={`badge ${getSeverityColor(job.severity)}`}>
                      <AlertCircle className="w-3 h-3 inline mr-1" />
                      {job.severity}
                    </span>
                    <span className={`badge ${getStatusColor(job.status)}`}>
                      {job.status.replace('_', ' ')}
                    </span>
                    {job.trade_type && (
                      <span className="badge bg-purple-100 text-purple-800 capitalize">
                        <Bot className="w-3 h-3 inline mr-1" />
                        {job.trade_type}
                      </span>
                    )}
                    {job.photos && job.photos.length > 0 && (
                      <span className="badge badge-secondary">
                        <ImageIcon className="w-3 h-3 inline mr-1" />
                        {job.photos.length} photo{job.photos.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {format(new Date(job.created_at), 'MMM dd, yyyy')}
                    </span>
                    {job.estimated_cost && (
                      <span className="flex items-center gap-1 font-medium text-gray-900">
                        <DollarSign className="w-3 h-3" />
                        ${job.estimated_cost.toFixed(2)}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          )
        ) : activeTab === 'pm_review' ? (
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <h3 className="font-bold text-red-900 mb-2">⚠️ These jobs need your attention</h3>
              <p className="text-red-700 text-sm">
                Landlords declined these jobs due to cost. Find cheaper solutions or negotiate scope.
              </p>
            </div>

            {pmReviewJobs.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <p className="text-gray-500">No jobs requiring PM action</p>
                </CardContent>
              </Card>
            ) : (
              pmReviewJobs.map((job) => (
                <Card 
                  key={job.id} 
                  className="border-l-4 border-red-500 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => {
                    setSelectedJob(job);
                    setShowDetailModal(true);
                  }}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <DollarSign className="w-5 h-5 text-red-600" />
                          <h3 className="font-bold text-lg text-gray-900">{job.title}</h3>
                        </div>
                        <p className="text-gray-600 text-sm mb-3">{job.property_address}, {job.property_suburb}</p>
                        <p className="text-gray-700">{job.description}</p>
                      </div>
                      {job.is_critical && (
                        <span className="bg-red-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                          URGENT
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4 bg-gray-50 p-4 rounded-lg">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Original Quote</p>
                        <p className="text-lg font-bold text-gray-900">
                          ${job.estimated_cost?.toFixed(2) || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Landlord's Max Budget</p>
                        <p className="text-lg font-bold text-red-600">
                          ${job.landlord_max_budget?.toFixed(2) || 'Not provided'}
                        </p>
                      </div>
                    </div>

                    {job.ai_cost_reduction_suggestions && job.ai_cost_reduction_suggestions.length > 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <p className="text-sm font-semibold text-blue-900 mb-2">💡 AI Suggestions:</p>
                        <ul className="text-sm text-blue-800 space-y-1">
                          {job.ai_cost_reduction_suggestions.slice(0, 2).map((suggestion, idx) => (
                            <li key={idx}>• {suggestion}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-4 border-t">
                      <span className={`text-sm font-medium ${
                        job.is_critical ? 'text-red-600' :
                        job.is_overdue ? 'text-orange-600' :
                        'text-gray-600'
                      }`}>
                        {job.days_since_declined} days since declined
                        {job.is_critical && ' - URGENT'}
                        {job.is_overdue && !job.is_critical && ' - Overdue'}
                      </span>
                      <Button size="sm" variant="outline">
                        Take Action →
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        ) : activeTab === 'history' ? (
          historyJobs.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Wrench className="w-16 h-16 text-gray-300 mb-4" />
                <p className="text-gray-500">No completed jobs yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {historyJobs.map((job) => (
                <Card 
                  key={job.id} 
                  className="card-hover cursor-pointer opacity-90 hover:opacity-100"
                  onClick={() => {
                    setSelectedJob(job);
                    setShowDetailModal(true);
                  }}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Wrench className="w-5 h-5 text-gray-400" />
                        <h3 className="font-semibold text-gray-700">{job.title}</h3>
                      </div>
                      <span className={`badge ${
                        job.status === 'closed' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {job.status}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">{job.description}</p>
                    
                    <div className="flex items-center gap-2 flex-wrap mb-3">
                      {job.trade_type && (
                        <span className="badge badge-secondary capitalize">
                          {job.trade_type}
                        </span>
                      )}
                      {job.final_cost && (
                        <span className="badge badge-success">
                          <DollarSign className="w-3 h-3 inline mr-1" />
                          ${job.final_cost.toFixed(2)}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Closed: {job.closed_at && format(new Date(job.closed_at), 'MMM dd, yyyy')}
                      </span>
                      {job.assigned_contractor_name && (
                        <span className="text-gray-600">
                          By: {job.assigned_contractor_name}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )
        ) : null}

        <AddMaintenanceModal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={loadJobs}
          userRole={user?.role}
        />

        {selectedJob && (
          <JobDetailModal
            open={showDetailModal}
            onClose={() => {
              setShowDetailModal(false);
              setSelectedJob(null);
            }}
            job={selectedJob}
            onSuccess={loadJobs}
            currentUser={user}
          />
        )}
      </div>
    </Layout>
  );
}